/**
 * @file Renders a modal to confirm certain profile actions. The current
 * profile actions that use this modal are:
 *   * Block
 *   * Hide
 *   * Unmatch
 */

import React, { Component, Fragment } from "react";
import classnames from "classnames";
import Modal from "shared/components/Modal";
import {
	OKEVENT,
	triggerOkEvent,
} from "shared/util/OkEventHelpers";
import _str from "shared/util/_str";
import _strf from "shared/util/_strf";

// A simple pub sub system to pass state to this component.
const subs = [];
const subscribe = (done) => subs.push(done);

// Some constants for each modal.
const types = {
	block: {
		title: _str("Block this person"),
	},
	hide: {
		title: _str("Hide this person"),
	},
	unmatch: {
		noClose: true,
		layout: "icon",
	},
	ocs_pass: {
		noClose: true,
		layout: "icon",
	},
	ocs_like: {
		noClose: true,
		layout: "icon",
	},
};

class ConfirmationModal extends Component {
	state = {
		onAction: () => false,
		onShow: () => false,
		show: false,
		type: undefined,
	};

	componentDidMount() {
		subscribe((state) => this.setState(state));
	}

	componentDidUpdate(prevProps, prevState) {
		const { show, onShow } = this.state;

		if (!prevState.show && show && onShow) {
			onShow();
		}
	}

	_close = () => {
		this.setState({ show: false });

		if (this.state.type === "hide") {
			triggerOkEvent(OKEVENT.CancelHide);
		} else if (this.state.type === "block") {
			triggerOkEvent(OKEVENT.CancelBlock);
		} else if (this.state.type === "unmatch") {
			triggerOkEvent(OKEVENT.CancelUnmatch);
		} else if (this.state.type === "ocs_pass") {
			triggerOkEvent(OKEVENT.PassCanceled);
		} else if (this.state.type === "ocs_like") {
			triggerOkEvent(OKEVENT.VoteCanceled);
		}
	};

	_closeAndTakeAction = () => {
		this._close();
		this.state.onAction();
	};

	_renderUnmatchChildren = () => {
		return (
			<Fragment>
				<div className="icon unmatch-icon" />

				<h2 className="title">
					{_strf("Unmatch?")}
				</h2>

				<p className="desc">
					{_strf("You won’t be able to see or message one another.")}
				</p>

				<button
					onClick={this._closeAndTakeAction}
					className="confirmationModal-action flatbutton red"
				>
					{_str("Unmatch")}
				</button>

				<button
					onClick={this._close}
					className="confirmationModal-cancel"
				>
					{_str("Cancel")}
				</button>
			</Fragment>
		);
	};

	_renderBlockChildren = () => {
		return (
			<div>
				<p>
					{_strf(`Is this person bothering you? Blocking hides you from them
						and prevents further messages from being exchanged.`)}
				</p>

				<p>
					{_strf("If you're being harassed by this person, please report them.")}
				</p>

				<button
					onClick={this._closeAndTakeAction}
					className="confirmationModal-action flatbutton red"
				>
					{_str("Yes, please")}
				</button>

				<button
					onClick={this._close}
					className="confirmationModal-cancel flatbutton silver"
				>
					{_str("Cancel")}
				</button>
			</div>
		);
	};

	_renderHideChildren = () => {
		return (
			<div>
				<p>
					{_str(`Not into them? Hiding prevents you from being shown to one
						another in places like QuickMatch and Browse Matches.`)}
				</p>

				<p>
					{_str("If you want to stop all further communication, block them.")}
				</p>

				<button
					onClick={this._closeAndTakeAction}
					className="confirmationModal-action flatbutton red"
				>
					{_str("Hide")}
				</button>

				<button
					onClick={this._close}
					className="confirmationModal-cancel flatbutton silver"
				>
					{_str("Cancel")}
				</button>
			</div>
		);
	};

	_renderOcsLikeChildren = () => {
		return (
			<Fragment>
				<div className="icon ocs-like-icon" />

				<h2 className="title">
					{_str("Like?")}
				</h2>

				<p className="desc">
					{_str("If you press this button, we’ll let them know you like them. Sound good?")}
				</p>

				<button
					onClick={this._closeAndTakeAction}
					className="confirmationModal-action flatbutton blue"
				>
					{_str("Like")}
				</button>

				<button
					onClick={this._close}
					className="confirmationModal-cancel"
				>
					{_str("I’ve made a mistake")}
				</button>
			</Fragment>
		);
	};

	_renderOcsPassChildren = () => {
		return (
			<Fragment>
				<div className="icon ocs-pass-icon" />

				<h2 className="title">
					{_str("Pass?")}
				</h2>

				<p className="desc">
					{_str("This means you won’t see this person again (unless you run out of better options). Are you sure you want to pass?")}
				</p>

				<button
					onClick={this._closeAndTakeAction}
					className="confirmationModal-action flatbutton blue"
				>
					{_str("Pass")}
				</button>

				<button
					onClick={this._close}
					className="confirmationModal-cancel"
				>
					{_str("Hold the phone")}
				</button>
			</Fragment>
		);
	};

	render() {
		const { show, type } = this.state;
		const config = types[type];

		if (!config) {
			return null;
		}

		return (
			<Modal
				onCancel={this._close}
				title={config.title}
				noClose={config.noClose}
				show={show}
				className={classnames("confirmationModal", {
					[`layout-${config.layout}`]: config.layout,
				})}
				id="confirmationModal"
				willCloseOnClick
			>
				{type === "hide" && this._renderHideChildren()}
				{type === "block" && this._renderBlockChildren()}
				{type === "unmatch" && this._renderUnmatchChildren()}
				{type === "ocs_like" && this._renderOcsLikeChildren()}
				{type === "ocs_pass" && this._renderOcsPassChildren()}
			</Modal>
		);
	}
}

export function dispatch(state) {
	subs.forEach((sub) => sub(state));
}

export default ConfirmationModal;



// WEBPACK FOOTER //
// ./src/shared/components/ConfirmationModal.jsx